<template>
  <div class="px-2 px-sm-4 pb-2 pt-3">
    <!-- Portal form navigation buttons to footer -->
    <portal to="footer-left" v-if="!isDialog && !isNestedForm">
      <v-btn text link color="secondary" @click="goBack" class="mr-4">
        <v-icon small left>
          mdi-chevron-left
        </v-icon>
        {{ $t('buttons.previous') }}
      </v-btn>
    </portal>
    <portal to="footer-additional-options" v-if="!isDialog && !isNestedForm">
      <v-btn color="primary" class="px-4 py-2" @click="goNext">
        {{
          $t(`buttons.${isOnLastStepOfForm || isFromShow ? 'submit' : 'next'}`)
        }}
        <v-icon small right>
          {{
            isOnLastStepOfForm || isFromShow ? 'mdi-check' : 'mdi-chevron-right'
          }}
        </v-icon>
      </v-btn>
    </portal>

    <template v-if="hasSlot('form.title')">
      <v-row class="mb-0">
        <v-col class="pl-1">
          <div>
            <slot name="form.title" :localFormData="localValue" />
          </div>
        </v-col>
      </v-row>
    </template>
    <v-window v-if="localValue" v-model="currentFormStep" class="mb-4">
      <v-window-item
        v-for="(windowForm, windowFormIndex) in computedForm"
        :value="windowFormIndex"
        :key="windowFormIndex"
      >
        <v-container fluid class="pa-0">
          <v-row>
            <template v-for="(formElement, index) in windowForm">
              <template v-if="isSection(formElement)">
                <v-expansion-panels
                  accordion
                  focusable
                  class="mt-6"
                  :key="`'container-element-${index}`"
                >
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div>
                        <v-icon>{{ formElement.section.icon }}</v-icon>
                        {{ capitalize(formElement.section.title) }}
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="pt-4">
                      <BaseFormLine
                        v-for="(field, i) in formElement.section.fields"
                        :key="`section-${index}-field-${i}`"
                        v-model="localValue"
                        :show-data="showData"
                        :formElement="field"
                        :component-type="formElementType(field)"
                        :resource="resource"
                        v-on="$listeners"
                        v-bind="$attrs"
                      >
                        <template
                          v-for="(_, scopedSlotName) in $scopedSlots"
                          #[scopedSlotName]="slotData"
                        >
                          <slot :name="scopedSlotName" v-bind="slotData" />
                        </template>
                        <template v-for="(_, slotName) in $slots" #[slotName]>
                          <slot :name="slotName" />
                        </template>
                      </BaseFormLine>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </template>
              <template v-else>
                <template v-if="formElement.virtual">
                  <BaseFormLine
                    v-model="localDataValue"
                    :show-data="showData"
                    :key="`'container-element-${index}`"
                    :form-element="formElement"
                    :component-type="formElementType(formElement)"
                    :resource="resource"
                    v-bind="eventListeners"
                  >
                    <template
                      v-for="(_, scopedSlotName) in $scopedSlots"
                      #[scopedSlotName]="slotData"
                    >
                      <slot :name="scopedSlotName" v-bind="slotData" />
                    </template>
                    <template v-for="(_, slotName) in $slots" #[slotName]>
                      <slot :name="slotName" />
                    </template>
                  </BaseFormLine>
                </template>
                <template v-else>
                  <BaseFormLine
                    v-model="localValue"
                    :show-data="showData"
                    :key="`'container-element-${index}`"
                    :form-element="formElement"
                    :component-type="formElementType(formElement)"
                    :resource="resource"
                    v-on="$listeners"
                    v-bind="$attrs"
                  >
                    <template
                      v-for="(_, scopedSlotName) in $scopedSlots"
                      #[scopedSlotName]="slotData"
                    >
                      <slot :name="scopedSlotName" v-bind="slotData" />
                    </template>
                    <template v-for="(_, slotName) in $slots" #[slotName]>
                      <slot :name="slotName" />
                    </template>
                  </BaseFormLine>
                </template>
              </template>
              <template v-if="formElement.show && formElement.singleLine">
                <div
                  :key="`'new-line-step-${windowFormIndex}-element-${index}`"
                  class="single-line"
                ></div>
              </template>
            </template>
          </v-row>
        </v-container>
      </v-window-item>
    </v-window>
  </div>
</template>

<script>
import { get, has, cloneDeep, findLast, capitalize } from 'lodash';
import hasSlot from '@/mixins/hasSlot';
import restructureFormElements from '@/mixins/restructureFormElements';
import { mapGetters, mapActions } from 'vuex';
import { getGlobalRegisteredComponents } from '@/components/forms/inputs/_global';

export default {
  mixins: [hasSlot, restructureFormElements],
  //DEV NOTE: Unify with BaseFormElement for compoenents. For now need to keep the same components here and in BaseFormElement
  /*
    formElement example: 
    {
      key: 'userId',
      as: 'searchInput',
      virtual: true/false, if you want the variable to be a virtual local variable (not to be inserted to formData). Can act as a helper
      label: this.$t('relationships.leave_request.user'),
      labelRequired: true,
      noLabel: true, //optional, by default is false
      onlyLabel: false //only renders the BaseLabelElement, and not the show/form element components
      singleLine: false //when set to true, the next form element will go to the next line (not by default next to the previous one)
      showBorder: true //currently only works on formElement slots, when set to true we can display the table-like bottom border
      col: 8,
      sm: 4,
      fullWidth: true, //when set to true sets cols to 12, other size custom settings (cols, sm, md ...) dont work,
      object: {
        //your props and values in camelCase
        indexServiceAction: this.userIndexServiceAction,
        etc...
      },
      listeners: {
        'blur': () => this.myFunction,
        'change:object': (object) => this.myFunctionWithArgument(object),
      }
    }
  */
  props: {
    value: {
      type: null,
      required: false,
    },
    form: {
      type: Array,
      required: true,
    },
    initialData: {
      type: Object,
      default: undefined,
    },
    showData: {
      type: Object,
      default: undefined,
    },
    fromFormLayout: {
      type: Boolean,
      default: false,
    },
    step: {
      type: Number,
      default: undefined,
    },
    customResource: {
      type: String,
      default: undefined,
    },
    //  our screen breakpoint where we specify default size of form. Available values: xs, sm, md, lg, xl
    screenBreakpoint: {
      type: String,
      default: undefined,
    },
    customSubmitButtonText: {
      type: String,
      default: undefined,
    },
    isNestedForm: {
      type: Boolean,
      default: false,
    },
    //  localData, doesnt affect formData (should be used with .sync)
    localData: {
      type: Object,
      default: undefined,
    },
    validateServiceAction: {
      type: Function,
      default: undefined,
    },
    action: {
      type: String,
      default: undefined,
    },
    isDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localAvailableComponents: {
        EmployeeSelect: () => import(`@/components/select/EmployeeSelect.vue`),
        AccountSelect: () => import(`@/components/select/AccountSelect.vue`),
        JobSelect: () => import(`@/components/select/JobSelect.vue`),
        ProjectSelect: () => import(`@/components/select/ProjectSelect.vue`),
        GroupBySelect: () => import('@/components/select/GroupBySelect.vue'),
        CustomChip: () => import('@/components/CustomChip.vue'),
        TextEditor: () => import(`@/components/TextEditor.vue`),
        DefaultLocaleSelect: () =>
          import(`@/components/select/DefaultLocaleSelect.vue`),
        CustomFields: () => import(`@/components/forms/CustomFields.vue`),
        SearchInputPreferred: () =>
          import(`@/components/select/SearchInputPreferred.vue`),
        IntermissionInput: () => import('@/components/IntermissionInput.vue'),
        AttachmentUploader: () => import('@/components/AttachmentUploader.vue'),
      },
      localData1: undefined,
      localFormData: undefined,
      resource: undefined, //what module (e.g. account, contact, project etc.) we are under. Used for finding automatic translation text
      availableComponents: [],
      currentStep: 0,
      validationErrors: [],
      pendingAction: null, // 'validate' | 'submit' | null
    };
  },
  computed: {
    ...mapGetters('app', [
      'previousRoute',
      'previousRouteName',
      'currentRouteName',
    ]),
    eventListeners() {
      // exclude `input`-listener otherwise the input listener from localDataValue
      // will interfere with localValue
      const { input, ...listeners } = this.$listeners;
      return listeners;
    },
    localDataValue: {
      get() {
        return this.localData || this.localData1;
      },
      set(v) {
        this.localData1 = v;
        this.$emit('update:localData', v);
      },
    },
    localValue: {
      get() {
        return this.value || this.localFormData;
      },
      set(v) {
        this.localFormData = v;
        this.$emit('input', v);
      },
    },
    currentFormStep: {
      get() {
        return this.currentStep;
      },
      set(v) {
        if (this.isNestedForm) return;
        this.goToStep(v);
        this.$emit('update:step', v);
      },
    },
    lastStep() {
      return this.computedForm.length - 1;
    },
    isOnFirstStepOfForm() {
      return this.currentStep === 0;
    },
    isOnLastStepOfForm() {
      return this.currentStep === this.lastStep;
    },
    isValidationRequired() {
      return !!this.validateServiceAction;
    },
    isFromShow() {
      return (
        this.$route?.query?.isFromShow === 'true' ||
        this.$route?.query?.isFromShow === true
      );
    },
    formDatabaseFields() {
      let currentFormFields = this.computedForm[this.currentStep];
      let dbFields = currentFormFields.filter(
        field => !(field.onlyShow || field.sectionTitle)
      );
      return dbFields;
    },
    hasCurrentStepValidationErrors() {
      let fieldNames = this.formDatabaseFields.map(field => field.key);
      return this.validationErrors.some(
        error =>
          fieldNames.includes(error.field) ||
          fieldNames.includes(error.field + 'Id')
      );
    },
    currentStepValidationErrors() {
      let fieldNames = this.formDatabaseFields.map(field => field.key);
      return this.validationErrors.filter(
        error =>
          fieldNames.includes(error.field) ||
          fieldNames.includes(error.field + 'Id')
      );
    },
  },
  watch: {
    currentFormStep(newVal, oldVal) {
      if (newVal != oldVal) this.$emit('update:step', newVal);

      if (newVal > oldVal) {
        this.$emit('change:step:increment', newVal);
      } else if (newVal < oldVal) {
        this.$emit('change:step:decrement', newVal);
      }
    },
    step: {
      handler(val) {
        if (typeof val !== 'undefined' && val != this.currentFormStep) {
          this.currentFormStep = val; //set form step to given step
          this.$emit(
            'reset:step',
            true
          ); /*reset initially given step to allow to do step again from ModuleInput */
        }
      },
    },
    pendingAction: {
      handler(action) {
        if (action === 'validate') {
          this.handleValidateWrapper();
        } else if (action === 'submit') {
          this.handleSubmit();
        }
      },
    },
    validationErrors: {
      deep: true,
      handler(newErrors, oldErrors) {
        if (this.isNestedForm) return;

        // Only process if we have current step validation errors
        if (this.hasCurrentStepValidationErrors) {
          this.setMessage({
            type: 'error',
            message: this.currentStepValidationErrors,
          });
        } else if (oldErrors.length > 0 && newErrors.length === 0) {
          // Errors were cleared, proceed to next step if we were validating
          this.resetMessages();
          if (this.pendingAction === 'validate') {
            this.goToNextStep();
          }
        }
      },
    },
  },
  created() {
    this.availableComponents = this.mergeListOfGlobalAndlocalRegisteredComponents();
    this.removePropsFromAttrs();
    this.initializeStep();
    this.findResourceForTranslation();
    window.addEventListener('keydown', this.formStepKeyboardNavigation);
  },
  mounted() {
    this.initialize();
    if (this.screenBreakpoint)
      this.$store.set('app/maxBreakpoint', this.screenBreakpoint);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.formStepKeyboardNavigation);
  },
  methods: {
    get,
    has,
    findLast,
    capitalize,
    ...mapActions('message', ['setMessage', 'resetMessages']),
    goToNextStep() {
      this.goToStep(this.currentStep + 1);
    },
    goToPreviousStep() {
      this.goToStep(this.currentStep - 1);
    },
    goToStep(num = 0) {
      if (this.isValidationRequired && this.hasCurrentStepValidationErrors) {
        return false;
      }

      let number = Number(num);
      if (number >= 0 && number <= this.lastStep) {
        this.currentStep = number;
        // Clear validation errors when moving to a new step
        this.validationErrors = [];
        return true;
      }

      // Handle submit when going beyond last step
      if (number === this.lastStep + 1) {
        this.pendingAction = 'submit';
      }
      return false;
    },

    goBack() {
      if (!this.isFromShow) {
        //fixes many general routing and purchase order send routing issues.
        //DEVNOTE: Known bug: normal edit form operation, go to send and then click previous, infinite route previous loop
        //DEVNOTE: Known bug: normal new form operation, go to send and then click previous,
        // then click next, then click previous -> infinite route previous loop
        //DEVNOTE: Refactor, should have a variable so we know that we entered the form from a BaseSection Edit,
        //  so we can immediately do history.back()
        if (
          this.currentRouteName == 'AdminPurchaseOrderSend' &&
          this.previousRouteName == 'AdminPurchaseOrderNew'
        ) {
          this.goToBackRoute();
          return;
        } else if (!this.isOnFirstStepOfForm) {
          this.goToPreviousStep();
          return;
        }
      }

      if (
        this.previousRouteName != 'login' &&
        this.previousRoute?.path != '/'
      ) {
        history.back();
      } else {
        //go to dashboard when we dont have a previous route
        this.$router.push({ name: 'OC' });
      }
    },
    goNext() {
      // If we are from BaseSection (isFromShow), submit immediately
      if (this.isFromShow) {
        this.pendingAction = 'submit';
        return;
      }

      // If we're on the last step, submit directly
      if (this.isOnLastStepOfForm) {
        this.pendingAction = 'submit';
        return;
      }

      // For all other steps, validate first if validation is required
      if (this.isValidationRequired) {
        this.pendingAction = 'validate';
      } else {
        this.goToNextStep();
      }
    },
    goToBackRoute() {
      const { backRoute } = this.$route.meta;
      if (backRoute === undefined) return this.$router.back();
      if (Number.isInteger(backRoute)) return this.$router.go(backRoute);
      return this.$router.push(backRoute);
    },
    formStepKeyboardNavigation(event) {
      const activeElementsBlockNavigation = [
        'text',
        'textarea',
        'input',
        'dialog',
      ];

      const activeElement = document.activeElement;

      if (activeElement) {
        const elementType = activeElement.type?.toLowerCase() || '';
        const elementTagName = activeElement.tagName?.toLowerCase() || '';
        const elementRole = activeElement.role?.toLowerCase() || '';

        if (
          activeElementsBlockNavigation.includes(elementType) ||
          activeElementsBlockNavigation.includes(elementTagName) ||
          activeElementsBlockNavigation.includes(elementRole) ||
          activeElement.isContentEditable
        ) {
          return;
        }
      }

      if (event.key === 'ArrowLeft') {
        if (!this.isOnFirstStepOfForm) this.goBack();
      } else if (event.key === 'ArrowRight') {
        if (!this.isOnLastStepOfForm) this.goNext();
      }
    },
    mergeListOfGlobalAndlocalRegisteredComponents() {
      // Extract keys from the local components object
      const localComponents = Object.keys(this.$options.components) || [];
      const globalComponents = getGlobalRegisteredComponents();

      // Merge the keys and the array
      return localComponents.concat(globalComponents);
    },
    formElementType(formElement) {
      const PREFIX_FILE_NAME = 'Base';

      if (!formElement.as) return undefined;

      if (this.isVueComponent(formElement.as)) return formElement.as;

      if (typeof formElement.as === 'string') {
        let formComponentName = this.toPascalCase(formElement.as);

        // First, look for the component in the localAvailableComponents mapping
        if (this.localAvailableComponents[formComponentName]) {
          return this.localAvailableComponents[formComponentName];
        }

        // Next, check for the component with the Base prefix
        let prefixed = PREFIX_FILE_NAME + formComponentName;
        if (this.localAvailableComponents[prefixed]) {
          return this.localAvailableComponents[prefixed];
        }

        // Finally, check if the component was registered globally
        const globalComponents = getGlobalRegisteredComponents();
        if (
          globalComponents.includes(formComponentName) ||
          globalComponents.includes(prefixed)
        ) {
          return formComponentName;
        }

        console.error(
          'Component not found in available components:',
          formComponentName
        );
      }
      return undefined;
    },
    toPascalCase(text) {
      return text.replace(/(^\w|-\w)/g, this.clearAndUpper);
    },
    clearAndUpper(text) {
      return text.replace(/-/, '').toUpperCase();
    },
    isVueComponent(obj) {
      return typeof obj === 'object' && (obj.render || obj.__file);
    },
    isSection(formElement) {
      return has(formElement, 'section');
    },
    async initialize() {
      if (Object.keys(this.initialData || {}).length > 0) {
        this.$set(this, 'localValue', await cloneDeep(this.initialData));
      } else {
        this.$set(this, 'localValue', {});
      }
      if (!this.localData) this.$set(this, 'localDataValue', {});

      this.$emit('formData:initialized', true);
      this.$emit('showData', this.showData);
    },
    //get resource from customResource provided by the dev or automatically from the route or parent routes
    //going to be used as a key in the translation file to know under which section we are going to look for to auto-translate the labels
    findResourceForTranslation() {
      if (this.customResource) {
        this.resource = this.customResource;
      } else {
        var resourceRoute = this.findLast(this.$route.matched, function(route) {
          return route.meta.resource;
        });
        this.resource = resourceRoute.meta?.resource;
      }
    },
    initializeStep() {
      //always convert to number as this.$route.query?.step sometimes is of string type
      this.currentStep = parseInt(
        this.step || this.$route.query?.step || 0,
        10
      );
    },
    removePropsFromAttrs() {
      delete this.$attrs['loading'];
    },
    async handleValidateWrapper() {
      if (!this.isValidationRequired) {
        this.pendingAction = null;
        this.goToNextStep();
        return;
      }

      try {
        // Call the validation through the parent component (BaseFormLayout)
        // which will use the form mixin's handleValidate method
        this.$emit('validate', this.localValue, data => {
          this.validationErrors = data?.errors || [];

          // If no validation errors, proceed to next step
          if (!this.hasCurrentStepValidationErrors) {
            this.resetMessages();
            this.goToNextStep();
          } else {
            // Show validation errors
            this.setMessage({
              type: 'error',
              message: this.currentStepValidationErrors,
            });
          }
          this.pendingAction = null;
        });
      } catch (e) {
        this.setMessage({
          type: 'error',
          message: e,
        });
        this.pendingAction = null;
      }
    },
    handleSubmit() {
      this.pendingAction = null;
      this.$emit('input', this.localValue);
      this.$emit('submit', this.localValue);
    },
  },
};
</script>
<style lang="css" scoped>
.expansion-panel-header-content {
  display: inline-flex;
  gap: 12px;
  padding-top: 8px;
  padding-bottom: 8px;
}
.single-line {
  width: 100%;
  flex-basis: 100%;
}
</style>
